import { Logic } from '~common/ui-logic/logic'
import { EventEmitter } from '~common/utils/events'
import type {
    SimpleTabsLogicDeps,
    SimpleTabsState,
    Tab,
    Pane,
    MatchSearchProps,
    EmailPreviewProps,
} from './types'
import type { DateRange } from 'react-day-picker'
import type { GlobalSearchState } from '~/services/cache/types'
import { sleep } from '~common/utils/sleep'
import { Email } from '~common/storage/email'
import { i18n } from '~/i18n/config'
import { GlobalEvent } from '~/types/global-events'
import { SIDEBAR_WIDTH } from './constants'
import React from 'react'
import { MatchSearch } from '~/features/match-search'
import { EmailAndInvoicePreview } from '~/features/email-preview/ui'
import type { Services } from '~/services/types'
import type { StorageModules } from '~/storage/types'

export interface SpawnPaneEvent {
    render: () => React.ReactNode
    title: string
}

export class SimpleTabsLogic extends Logic<
    SimpleTabsLogicDeps,
    SimpleTabsState
> {
    private activeTimeRef: {
        startTime: number | null
        visibilityStartTime: number | null
        lastActivityTime: number
        accumulatedSeconds: number
        visibilityTimeout: ReturnType<typeof setTimeout> | null
        idleTimeout: ReturnType<typeof setTimeout> | null
    } = {
        startTime: null,
        visibilityStartTime: null,
        lastActivityTime: Date.now(),
        accumulatedSeconds: 0,
        visibilityTimeout: null,
        idleTimeout: null,
    }

    private saveToStorageInterval: ReturnType<typeof setInterval> | null = null

    constructor(deps: SimpleTabsLogicDeps) {
        super(deps)
    }

    getInitialState(): SimpleTabsState {
        const savedOpen = localStorage.getItem('split-tabs-sidebar-open')
        const isSidebarOpen = savedOpen !== null ? JSON.parse(savedOpen) : true

        const savedActiveTime = localStorage.getItem('split-tabs-active-time')
        const activeTimeSeconds =
            savedActiveTime !== null ? Number.parseInt(savedActiveTime, 10) : 0

        // Read routes from URL query string
        const routesFromUrl = this.getRoutesFromUrl()
        const initialPaneIds =
            routesFromUrl.length > 0
                ? routesFromUrl
                : this.deps.initialTabId
                  ? [this.deps.initialTabId]
                  : []

        const savedSplitPosition = localStorage.getItem(
            'split-tabs-pane-split-position',
        )
        const paneSplitPosition =
            savedSplitPosition !== null
                ? Number.parseFloat(savedSplitPosition)
                : 50

        const initialFocused =
            initialPaneIds[0] || this.deps.initialTabId || null

        return {
            activePaneIds: initialPaneIds,
            spawnedPanes: [],
            focusedPaneId: initialFocused,
            isSidebarOpen,
            sidebarWidth: SIDEBAR_WIDTH,
            gmailSearchFilter: 'has-invoices',
            gmailTotalCount: 0,
            gmailHasInvoicesCount: 0,
            gmailHasExecutedSearch: false,
            searchTerms:
                this.deps.services.cache.getKey('searchState')?.global
                    ?.search || undefined,
            searchDateRange:
                this.deps.services.cache.getKey('searchState')?.global?.date ||
                undefined,
            activeTimeSeconds,
            paneSplitPosition,
            searchPlaceholder: this.computeSearchPlaceholder(initialFocused),
        }
    }

    async initialize(): Promise<void> {
        // Listen for spawn pane events from anywhere in the app
        // This is done in initialize() to ensure getState/setState methods are overridden by useLogic hook
        this.deps.services.globalEvents.listen((event) => {
            if (event.type === 'closeCurrentPane') {
                if (this.state.focusedPaneId) {
                    this.closePane(this.state.focusedPaneId)
                }
            }
            if (event.type === 'spawnPane') {
                this.spawnPane(
                    event.render,
                    event.title,
                    undefined,
                    event.matchSearchProps,
                    event.emailPreviewProps,
                )
            }
        })

        // Listen for cache updates to recalculate Gmail counts
        this.deps.services.cache.onUpdate.listen((keys) => {
            if (
                keys.has('emails') &&
                this.state.focusedPaneId === 'gmail-search'
            ) {
                this.updateGmailCounts()
            }
        })
        // Also listen to fine-grained email updates to keep counts fresh when email content changes
        this.deps.services.cache.onEmailsUpdate.listen(() => {
            if (this.state.focusedPaneId === 'gmail-search') {
                this.updateGmailCounts()
            }
        })

        // Initialize active time tracking
        this.activeTimeRef.accumulatedSeconds = this.state.activeTimeSeconds
        this.setupActiveTimeTracking()

        // Save to localStorage every minute
        this.saveToStorageInterval = setInterval(() => {
            this.saveActiveTimeToStorage()
        }, 60000)

        // Save to localStorage on page unload
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                this.saveActiveTimeToStorage()
            })
        }

        this.cleanupFns.push(() => {
            if (this.saveToStorageInterval) {
                clearInterval(this.saveToStorageInterval)
            }
        })
    }

    private updateActiveTime(seconds: number, saveToStorage = false) {
        this.setState({ activeTimeSeconds: seconds })
        if (saveToStorage) {
            localStorage.setItem('split-tabs-active-time', seconds.toString())
        }
    }

    private saveActiveTimeToStorage() {
        const currentSeconds = this.calculateCurrentActiveTime()
        localStorage.setItem(
            'split-tabs-active-time',
            currentSeconds.toString(),
        )
    }

    private calculateCurrentActiveTime(): number {
        const ref = this.activeTimeRef
        if (
            ref.startTime !== null &&
            ref.visibilityStartTime !== null &&
            typeof document !== 'undefined' &&
            !document.hidden
        ) {
            const now = Date.now()
            const elapsed = (now - ref.startTime) / 1000
            return ref.accumulatedSeconds + elapsed
        }
        return ref.accumulatedSeconds
    }

    private setupActiveTimeTracking() {
        if (typeof document === 'undefined') return

        const handleVisibilityChange = () => {
            const now = Date.now()
            const ref = this.activeTimeRef

            if (document.hidden) {
                // Tab became hidden - accumulate time and stop counting
                if (
                    ref.startTime !== null &&
                    ref.visibilityStartTime !== null
                ) {
                    // Only count time after the 3s offset has passed
                    const timeSinceVisibility = now - ref.visibilityStartTime
                    if (timeSinceVisibility > 3000) {
                        const elapsed = (now - ref.startTime) / 1000
                        ref.accumulatedSeconds += elapsed
                    }
                    ref.startTime = null
                    ref.visibilityStartTime = null
                    this.updateActiveTime(ref.accumulatedSeconds)
                    this.saveActiveTimeToStorage()
                }
                if (ref.visibilityTimeout) {
                    clearTimeout(ref.visibilityTimeout)
                    ref.visibilityTimeout = null
                }
                if (ref.idleTimeout) {
                    clearTimeout(ref.idleTimeout)
                    ref.idleTimeout = null
                }
            } else {
                // Tab became visible - wait 3s before starting to count
                ref.visibilityStartTime = now
                ref.visibilityTimeout = setTimeout(() => {
                    ref.startTime = Date.now()
                    ref.lastActivityTime = Date.now()
                    // Set initial idle timeout
                    ref.idleTimeout = setTimeout(() => {
                        if (ref.startTime !== null) {
                            const elapsed =
                                (ref.lastActivityTime - ref.startTime) / 1000
                            ref.accumulatedSeconds += elapsed
                            ref.startTime = null
                            ref.visibilityStartTime = null
                            this.updateActiveTime(ref.accumulatedSeconds)
                            this.saveActiveTimeToStorage()
                        }
                    }, 5000)
                }, 3000)
            }
        }

        const handleActivity = () => {
            const ref = this.activeTimeRef
            const now = Date.now()

            // Don't process activity if tab is hidden
            if (document.hidden) return

            // Reset idle timeout
            if (ref.idleTimeout) {
                clearTimeout(ref.idleTimeout)
            }

            // If we were idle (startTime is null) but visibility offset has passed, restart counting
            if (
                ref.visibilityStartTime !== null &&
                ref.startTime === null &&
                now - ref.visibilityStartTime > 3000
            ) {
                ref.startTime = now
            }

            ref.lastActivityTime = now

            // Set idle timeout (5s)
            ref.idleTimeout = setTimeout(() => {
                if (ref.startTime !== null) {
                    const elapsed =
                        (ref.lastActivityTime - ref.startTime) / 1000
                    ref.accumulatedSeconds += elapsed
                    ref.startTime = null
                    this.updateActiveTime(ref.accumulatedSeconds)
                    this.saveActiveTimeToStorage()
                }
            }, 5000)
        }

        // Initialize
        if (!document.hidden) {
            this.activeTimeRef.visibilityStartTime = Date.now()
            this.activeTimeRef.visibilityTimeout = setTimeout(() => {
                this.activeTimeRef.startTime = Date.now()
                this.activeTimeRef.lastActivityTime = Date.now()
                // Set initial idle timeout
                this.activeTimeRef.idleTimeout = setTimeout(() => {
                    const ref = this.activeTimeRef
                    if (ref.startTime !== null) {
                        const elapsed =
                            (ref.lastActivityTime - ref.startTime) / 1000
                        ref.accumulatedSeconds += elapsed
                        ref.startTime = null
                        ref.visibilityStartTime = null
                        this.updateActiveTime(ref.accumulatedSeconds)
                        this.saveActiveTimeToStorage()
                    }
                }, 5000)
            }, 3000)
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        document.addEventListener('mousemove', handleActivity)
        document.addEventListener('keydown', handleActivity)
        document.addEventListener('click', handleActivity)
        document.addEventListener('scroll', handleActivity)

        // Handle sidebar toggle keyboard shortcut (Cmd+# on Mac, Ctrl+# on Windows/Linux)
        const handleSidebarToggle = (event: KeyboardEvent) => {
            // Check for # key with modifier key
            // Mac: Cmd+#, Windows/Linux: Ctrl+#
            if (event.key === '#' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault()
                this.toggleSidebar()
            }
        }

        document.addEventListener('keydown', handleSidebarToggle)

        // Store cleanup functions
        this.cleanupFns.push(() => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            )
            document.removeEventListener('mousemove', handleActivity)
            document.removeEventListener('keydown', handleActivity)
            document.removeEventListener('click', handleActivity)
            document.removeEventListener('scroll', handleActivity)
            document.removeEventListener('keydown', handleSidebarToggle)
            if (this.activeTimeRef.visibilityTimeout) {
                clearTimeout(this.activeTimeRef.visibilityTimeout)
            }
            if (this.activeTimeRef.idleTimeout) {
                clearTimeout(this.activeTimeRef.idleTimeout)
            }
        })
    }

    /**
     * Read routes from URL query string
     * Returns array of valid tab IDs found in the URL
     */
    private getRoutesFromUrl(): string[] {
        if (typeof window === 'undefined') {
            return []
        }

        const params = new URLSearchParams(window.location.search)
        const routesParam = params.get('routes')
        if (!routesParam) {
            return []
        }

        // Parse comma-separated routes
        const routeIds = routesParam
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)

        // Filter to only include valid tab IDs (not spawned panes)
        const validTabIds = new Set(this.deps.tabs.map((tab) => tab.id))
        return routeIds.filter((id) => validTabIds.has(id))
    }

    /**
     * Update URL query string with current routes (only regular tabs, not spawned panes)
     * @param activePaneIds - Optional array of active pane IDs to sync. If not provided, uses current state.
     */
    private syncRoutesToUrl(activePaneIds?: string[]): void {
        if (typeof window === 'undefined') {
            return
        }

        // Filter out spawned panes - only persist regular tab IDs
        const validTabIds = new Set(this.deps.tabs.map((tab) => tab.id))
        const paneIdsToSync = activePaneIds ?? this.state.activePaneIds
        const regularTabIds = paneIdsToSync.filter((id) => validTabIds.has(id))

        const url = new URL(window.location.href)

        // Update or remove routes param
        if (regularTabIds.length > 0) {
            url.searchParams.set('routes', regularTabIds.join(','))
        } else {
            url.searchParams.delete('routes')
        }

        // Update URL without reload, preserving other query params
        window.history.replaceState({}, '', url.toString())
    }

    // Handle tab click with shift-key support
    handleTabClick(tabId: string, shiftKey: boolean) {
        if (shiftKey) {
            // If no panes are open, behave like a normal click (full width)
            if (this.state.activePaneIds.length === 0) {
                this.switchToTab(tabId)
            } else {
                // Otherwise open in a new split pane
                this.openTabInNewPane(tabId)
            }
        } else {
            // Regular click: switch to tab
            this.switchToTab(tabId)
        }
    }

    // Switch to a different tab in the focused pane, keeping the previous one alive in background
    switchToTab(tabId: string) {
        // Always fill the first (left) pane and collapse to single-pane view
        const newActivePaneIds = [tabId]
        this.setState({
            activePaneIds: newActivePaneIds,
            focusedPaneId: tabId,
        })
        this.updateSearchPlaceholder()
        this.syncRoutesToUrl(newActivePaneIds)
        // Update Gmail counts when switching to Gmail search tab
        if (tabId === 'gmail-search') {
            this.updateGmailCounts()
        } else {
            // Reset Gmail search state when switching away from Gmail search
            this.setState({
                gmailHasExecutedSearch: false,
            })
        }
    }

    toggleSidebar() {
        const newOpen = !this.state.isSidebarOpen
        this.setState({ isSidebarOpen: newOpen })

        // Defer non-critical operations to avoid blocking UI update
        requestAnimationFrame(() => {
            // Emit event after browser has a chance to paint
            this.deps.services.globalEvents.emit({
                type: 'sidebarStateChanged',
                isOpen: newOpen,
            })

            // Defer localStorage write (not critical for immediate UI feedback)
            if (typeof requestIdleCallback !== 'undefined') {
                requestIdleCallback(() => {
                    localStorage.setItem(
                        'split-tabs-sidebar-open',
                        JSON.stringify(newOpen),
                    )
                })
            } else {
                setTimeout(() => {
                    localStorage.setItem(
                        'split-tabs-sidebar-open',
                        JSON.stringify(newOpen),
                    )
                }, 0)
            }
        })
    }

    openSidebar() {
        if (!this.state.isSidebarOpen) {
            this.setState({ isSidebarOpen: true })

            // Defer non-critical operations to avoid blocking UI update
            requestAnimationFrame(() => {
                this.deps.services.globalEvents.emit({
                    type: 'sidebarStateChanged',
                    isOpen: true,
                })

                if (typeof requestIdleCallback !== 'undefined') {
                    requestIdleCallback(() => {
                        localStorage.setItem(
                            'split-tabs-sidebar-open',
                            JSON.stringify(true),
                        )
                    })
                } else {
                    setTimeout(() => {
                        localStorage.setItem(
                            'split-tabs-sidebar-open',
                            JSON.stringify(true),
                        )
                    }, 0)
                }
            })
        }
    }

    closeSidebar() {
        if (this.state.isSidebarOpen) {
            this.setState({ isSidebarOpen: false })

            // Defer non-critical operations to avoid blocking UI update
            requestAnimationFrame(() => {
                this.deps.services.globalEvents.emit({
                    type: 'sidebarStateChanged',
                    isOpen: false,
                })

                if (typeof requestIdleCallback !== 'undefined') {
                    requestIdleCallback(() => {
                        localStorage.setItem(
                            'split-tabs-sidebar-open',
                            JSON.stringify(false),
                        )
                    })
                } else {
                    setTimeout(() => {
                        localStorage.setItem(
                            'split-tabs-sidebar-open',
                            JSON.stringify(false),
                        )
                    }, 0)
                }
            })
        }
    }

    // Open a tab in a new pane (for shift-click functionality)
    openTabInNewPane(tabId: string) {
        // If the tab is already open, just focus it
        if (this.state.activePaneIds.includes(tabId)) {
            this.setState({ focusedPaneId: tabId })
            this.updateSearchPlaceholder()
            return
        }

        // If no panes, open as the first pane (full width)
        if (this.state.activePaneIds.length === 0) {
            const newActivePaneIds = [tabId]
            this.setState({
                activePaneIds: newActivePaneIds,
                focusedPaneId: tabId,
            })
            this.updateSearchPlaceholder()
            this.syncRoutesToUrl(newActivePaneIds)
            return
        }

        // If only one pane exists, open as the second pane (50/50 split)
        if (this.state.activePaneIds.length === 1) {
            const newActivePaneIds = [this.state.activePaneIds[0], tabId]
            this.setState({
                activePaneIds: newActivePaneIds,
                focusedPaneId: tabId,
            })
            this.updateSearchPlaceholder()
            this.syncRoutesToUrl(newActivePaneIds)
            return
        }

        // Otherwise push onto the right-side stack (do not replace the second pane)
        const newActivePaneIds = [...this.state.activePaneIds, tabId]
        this.setState({
            activePaneIds: newActivePaneIds,
            focusedPaneId: tabId,
        })
        this.updateSearchPlaceholder()
        this.syncRoutesToUrl(newActivePaneIds)
    }

    // Helper function to create a MatchSearch render function from props
    private createMatchSearchRender(
        props: MatchSearchProps,
    ): () => React.ReactNode {
        return () =>
            React.createElement(MatchSearch, {
                ...props,
                key: `match-search-${props.rootItem.id}-${props.itemType}`,
            })
    }

    // Helper function to create an EmailPreview render function from props
    private createEmailPreviewRender(
        props: EmailPreviewProps,
        services: Services,
        storageModules: StorageModules,
    ): () => React.ReactNode {
        return () =>
            React.createElement(EmailAndInvoicePreview, {
                ...props,
                services,
                storageModules,
                key: `email-preview-${props.invoiceId || props.emailId || 'unknown'}`,
            })
    }

    // Helper to determine pane type
    private getPaneType(
        title: string,
        matchSearchProps?: MatchSearchProps,
        emailPreviewProps?: EmailPreviewProps,
    ): 'match-search' | 'email-preview' | 'other' {
        if (matchSearchProps !== undefined) {
            return 'match-search'
        }
        if (emailPreviewProps !== undefined || title === 'Invoice Preview') {
            return 'email-preview'
        }
        return 'other'
    }

    // Spawn a new pane with custom content
    spawnPane(
        render: () => React.ReactNode,
        title: string,
        counter?: number,
        matchSearchProps?: MatchSearchProps,
        emailPreviewProps?: EmailPreviewProps,
    ): string {
        const paneType = this.getPaneType(
            title,
            matchSearchProps,
            emailPreviewProps,
        )

        // Handle email-preview: only one instance, replace if exists
        if (paneType === 'email-preview' && emailPreviewProps) {
            const existingPane = this.state.spawnedPanes.find(
                (pane) =>
                    pane.title === 'Invoice Preview' || pane.emailPreviewProps,
            )
            if (existingPane) {
                // Update the existing pane's props without incrementing version
                // This allows React to update the component with new props without unmounting it
                const updatedPanes = this.state.spawnedPanes.map((pane) =>
                    pane.id === existingPane.id
                        ? {
                              ...pane,
                              emailPreviewProps,
                              title, // Update title in case it changed
                              counter,
                              render: this.createEmailPreviewRender(
                                  emailPreviewProps,
                                  this.deps.services,
                                  this.deps.storageModules,
                              ),
                              // Don't increment version - keep same key so React updates without unmounting
                          }
                        : pane,
                )

                // Ensure the pane is in activePaneIds and focus it
                const activePaneIds = this.state.activePaneIds.includes(
                    existingPane.id,
                )
                    ? this.state.activePaneIds
                    : [...this.state.activePaneIds, existingPane.id]

                this.setState({
                    spawnedPanes: updatedPanes,
                    activePaneIds,
                    focusedPaneId: existingPane.id,
                })
                this.updateSearchPlaceholder()
                return existingPane.id
            }
        }

        // Handle match-search: only one instance, replace if exists
        if (paneType === 'match-search' && matchSearchProps) {
            const existingMatchPane = this.state.spawnedPanes.find(
                (pane) =>
                    pane.title.startsWith('Find matching') ||
                    pane.title.includes('matching'),
            )
            if (existingMatchPane) {
                // Update the existing pane's props without incrementing version
                // This allows React to update the component with new props without unmounting it
                // The component key stays the same (tabId-version), so React will update in place
                const updatedPanes = this.state.spawnedPanes.map((pane) =>
                    pane.id === existingMatchPane.id
                        ? {
                              ...pane,
                              matchSearchProps,
                              title, // Update title in case it changed
                              counter,
                              render: this.createMatchSearchRender(
                                  matchSearchProps,
                              ),
                              // Don't increment version - keep same key so React updates without unmounting
                          }
                        : pane,
                )

                // Ensure the pane is in activePaneIds and focus it
                const activePaneIds = this.state.activePaneIds.includes(
                    existingMatchPane.id,
                )
                    ? this.state.activePaneIds
                    : [...this.state.activePaneIds, existingMatchPane.id]

                this.setState({
                    spawnedPanes: updatedPanes,
                    activePaneIds,
                    focusedPaneId: existingMatchPane.id,
                })
                this.updateSearchPlaceholder()
                return existingMatchPane.id
            }
        }

        // Create a new pane if no existing pane was found
        const paneId = `spawned-${Date.now()}-${Math.random().toString(36).slice(2)}`
        const newPane: Pane = {
            id: paneId,
            render:
                matchSearchProps !== undefined
                    ? this.createMatchSearchRender(matchSearchProps)
                    : emailPreviewProps !== undefined
                      ? this.createEmailPreviewRender(
                            emailPreviewProps,
                            this.deps.services,
                            this.deps.storageModules,
                        )
                      : render,
            title,
            counter: counter,
            matchSearchProps,
            emailPreviewProps,
        }

        // Simply add the new pane - type-specific replacement is handled above
        // Both match-search and email-preview can coexist, but only one of each type
        this.setState({
            spawnedPanes: [...this.state.spawnedPanes, newPane],
            activePaneIds: [...this.state.activePaneIds, paneId],
            focusedPaneId: paneId,
        })
        this.updateSearchPlaceholder()
        return paneId
    }

    // Focus a specific pane
    focusPane(paneId: string) {
        this.setState({ focusedPaneId: paneId })

        if (this.state.activePaneIds.includes(paneId)) {
            // Update Gmail counts when focusing on Gmail search pane
            if (paneId === 'gmail-search') {
                this.updateGmailCounts()
            } else {
                // Reset Gmail search state when focusing away from Gmail search
                this.setState({
                    gmailHasExecutedSearch: false,
                })
            }
        }
        this.updateSearchPlaceholder()
    }

    // Close a pane (spawned or active tab)
    closePane(paneId: string) {
        // Remove from active panes if present
        const newActivePaneIds = this.state.activePaneIds.filter(
            (id) => id !== paneId,
        )

        // Update focused pane: if we closed the focused pane, focus the top of the right-side stack
        let newFocusedPaneId = this.state.focusedPaneId
        if (this.state.focusedPaneId === paneId) {
            newFocusedPaneId =
                newActivePaneIds.length > 0
                    ? newActivePaneIds[newActivePaneIds.length - 1]
                    : null
        }

        this.setState({
            activePaneIds: newActivePaneIds,
            spawnedPanes: this.state.spawnedPanes.filter(
                (pane) => pane.id !== paneId,
            ),
            focusedPaneId: newFocusedPaneId,
        })
        this.updateSearchPlaceholder()
        this.syncRoutesToUrl(newActivePaneIds)
    }

    // Get all panes (only regular tabs, spawned panes appear directly in split view)
    getAllPanes(): Array<{
        id: string
        title: string
        isSpawned: boolean
        counter?: number
    }> {
        const tabPanes = this.deps.tabs.map((tab) => ({
            id: tab.id,
            title: tab.title,
            isSpawned: false,
            counter: tab.counter,
        }))

        // Spawned panes don't appear in the tab bar - they appear directly in the split view
        return tabPanes
    }

    performSearch(
        terms?: string,
        dateRange?: DateRange,
        reset?: boolean,
        forceSend?: boolean,
    ) {
        let newTerms = terms
        let newDateRange = dateRange

        if (reset) {
            if (terms == null) {
                newTerms = undefined
            }
            if (dateRange == null) {
                newDateRange = undefined
            }
        }

        if (!forceSend && this.state.focusedPaneId === 'gmail-search') {
            return
        }

        if (this.state.focusedPaneId) {
            const searchObject = {
                type: 'performSearch',
                paneId: this.state.focusedPaneId,
                searchTerm: newTerms,
                dateRange: newDateRange,
            }
            this.deps.services.globalEvents.emit(searchObject as GlobalEvent)
            this.deps.services.cache.updateSearchState({
                global: {
                    search: newTerms,
                    date: newDateRange,
                },
            })
        }
    }

    // Get the appropriate search icon based on the focused pane
    getSearchIcon(): 'document' | 'gmail' | 'search' | 'bank' | undefined {
        if (this.state.focusedPaneId === 'invoices') {
            return 'document'
        } else if (this.state.focusedPaneId === 'gmail-search') {
            return 'gmail'
        } else if (this.state.focusedPaneId === 'transactions') {
            return 'bank'
        }
        return 'search'
        // Default to document if no pane is focused or unknown pane
    }

    private computeSearchPlaceholder(paneId: string | null): string {
        if (!paneId) {
            return i18n.t('placeholders.search', { ns: 'split-tabs' })
        }
        if (paneId.includes('invoices')) {
            return i18n.t('placeholders.searchInvoices', { ns: 'split-tabs' })
        } else if (paneId.includes('gmail-search')) {
            return i18n.t('placeholders.searchGmail', { ns: 'split-tabs' })
        } else if (paneId.includes('transactions')) {
            return i18n.t('placeholders.searchTransactions', {
                ns: 'split-tabs',
            })
        }
        return i18n.t('placeholders.search', { ns: 'split-tabs' })
    }

    private updateSearchPlaceholder(): void {
        const placeholder = this.computeSearchPlaceholder(
            this.state.focusedPaneId,
        )
        this.setState({ searchPlaceholder: placeholder })
    }

    // updateGmailSearchFilter(filter: 'has-invoices' | 'all') {
    //     // Only emit event if the filter is actually changing
    //     if (this.state.gmailSearchFilter === filter) {
    //         return
    //     }

    //     this.setState({
    //         gmailSearchFilter: filter,
    //     })
    // }

    updateGmailSearchExecuted(executed: boolean) {
        this.setState({
            gmailHasExecutedSearch: executed,
        })
    }

    updateGmailCounts() {
        const allEmails = this.deps.services.cache.getKey('emails')
        const emails = Object.values(allEmails).filter((email) => email != null)

        const totalCount = emails.length
        const hasInvoicesCount = emails.filter(
            (email: Email) => email.analysisStatus === 'full-success',
        ).length

        this.setState({
            gmailTotalCount: totalCount,
            gmailHasInvoicesCount: hasInvoicesCount,
        })
    }

    setPaneSplitPosition(position: number) {
        const clampedPosition = Math.max(20, Math.min(80, position))
        this.setState({ paneSplitPosition: clampedPosition })
        localStorage.setItem(
            'split-tabs-pane-split-position',
            clampedPosition.toString(),
        )
    }
}
